/**
 * SenseiChatContext – persists Ask Sensei chat (messages + suggested questions)
 * per patient/session across main and right-panel tab switches.
 * Key: `${patientId}-${sessionId ?? 'none'}`.
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { ChatMessage } from '../hooks/useMedicalChatbot';

type Slot = { messages: ChatMessage[]; suggestedQuestions: string[] };
type Store = Record<string, Slot>;

type SenseiChatContextType = {
  getSlot: (key: string) => Slot;
  addMessage: (key: string, message: ChatMessage) => void;
  setMessages: (key: string, messages: ChatMessage[]) => void;
  clearMessages: (key: string) => void;
  setSuggestedQuestions: (key: string, questions: string[]) => void;
};

const defaultSlot: Slot = { messages: [], suggestedQuestions: [] };

const SenseiChatContext = createContext<SenseiChatContextType | null>(null);

export function SenseiChatProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<Store>({});

  const getSlot = useCallback((key: string): Slot => {
    return store[key] ?? defaultSlot;
  }, [store]);

  const addMessage = useCallback((key: string, message: ChatMessage) => {
    setStore((prev) => {
      const slot = prev[key] ?? { ...defaultSlot };
      return {
        ...prev,
        [key]: { ...slot, messages: [...slot.messages, message] },
      };
    });
  }, []);

  const setMessages = useCallback((key: string, messages: ChatMessage[]) => {
    setStore((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? defaultSlot), messages },
    }));
  }, []);

  const clearMessages = useCallback((key: string) => {
    setStore((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? defaultSlot), messages: [] },
    }));
  }, []);

  const setSuggestedQuestions = useCallback((key: string, questions: string[]) => {
    setStore((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? defaultSlot), suggestedQuestions: questions },
    }));
  }, []);

  const value: SenseiChatContextType = {
    getSlot,
    addMessage,
    setMessages,
    clearMessages,
    setSuggestedQuestions,
  };

  return (
    <SenseiChatContext.Provider value={value}>
      {children}
    </SenseiChatContext.Provider>
  );
}

export function useSenseiChatContext() {
  return useContext(SenseiChatContext);
}

export function getSenseiChatKey(patientId: string | undefined, sessionId: string | undefined): string | null {
  if (!patientId) return null;
  return `${patientId}-${sessionId ?? 'none'}`;
}
